import {
  Box,
  Button,
  ButtonText,
  Heading,
  HStack,
  Input,
  InputField,
  Text,
  Textarea,
  TextareaInput,
  VStack,
} from '@gluestack-ui/themed';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { useDialog } from '@/common/context/DialogContext';
import type { AppToastCta } from '@/common/components/AppToast';
import { parseToastLabQuery } from '@/visual-lab/chapters/toast/parseToastLabQuery';
import {
  TOAST_LAB_DEFAULT_DURATION_MS,
  TOAST_LAB_PRESETS,
  type ToastLabAction,
  type ToastLabPresetId,
} from '@/visual-lab/chapters/toast/toastLabPresets';

const TOAST_ACTIONS: ToastLabAction[] = ['error', 'success', 'warning', 'info', 'attention'];
const PRESET_IDS: ToastLabPresetId[] = ['short', 'overflow', 'unbreakable', 'cta'];

function readInitialQuery() {
  if (typeof window === 'undefined') {
    return parseToastLabQuery('');
  }
  return parseToastLabQuery(window.location.search);
}

function ControlGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <VStack space="xs" flex={1} minWidth={220}>
      <Text size="xs" color="$textSubtle" fontWeight="semibold" letterSpacing={0.4}>
        {label.toUpperCase()}
      </Text>
      {children}
    </VStack>
  );
}

function PresetChip({
  active,
  label,
  onPress,
}: {
  active: boolean;
  label: string;
  onPress: () => void;
}) {
  return (
    <Button
      onPress={onPress}
      size="sm"
      variant={active ? 'solid' : 'outline'}
      action="secondary"
    >
      <ButtonText>{label}</ButtonText>
    </Button>
  );
}

/**
 * Toast Lab — fires production portal toasts on a high-contrast canvas for paint commissioning.
 */
export function ToastLabScreen() {
  const { showToastMessage } = useDialog();
  const initialQuery = readInitialQuery();
  const autoShownRef = useRef(false);

  const [action, setAction] = useState<ToastLabAction>(initialQuery.action);
  const [duration, setDuration] = useState(String(initialQuery.duration));
  const [title, setTitle] = useState(initialQuery.title);
  const [message, setMessage] = useState(initialQuery.message);
  const [activePreset, setActivePreset] = useState<ToastLabPresetId | null>(
    initialQuery.preset ?? null,
  );

  const showToast = useCallback((
    nextAction: ToastLabAction,
    nextMessage: string,
    nextTitle: string,
    nextDurationMs: number,
    cta?: AppToastCta,
  ) => {
    if (!nextMessage.trim()) {
      return;
    }
    const parsedDuration = Number.parseInt(String(nextDurationMs), 10);
    const durationMs = Number.isNaN(parsedDuration) || parsedDuration <= 0
      ? TOAST_LAB_DEFAULT_DURATION_MS
      : parsedDuration;
    if (cta) {
      showToastMessage(nextAction, nextMessage, nextTitle, 'top', durationMs, 'accent', cta);
      return;
    }
    showToastMessage(nextAction, nextMessage, nextTitle, 'top', durationMs);
  }, [showToastMessage]);

  const applyPreset = useCallback((presetId: ToastLabPresetId) => {
    const preset = TOAST_LAB_PRESETS[presetId];
    setActivePreset(presetId);
    setAction(preset.action);
    setTitle(preset.title);
    setMessage(preset.message);
    setDuration(String(TOAST_LAB_DEFAULT_DURATION_MS));
    showToast(
      preset.action,
      preset.message,
      preset.title,
      TOAST_LAB_DEFAULT_DURATION_MS,
      presetId === 'cta'
        ? {
          label: 'View contact',
          onPress: () => undefined,
          testID: 'connection-accepted-toast-view',
        }
        : undefined,
    );
  }, [showToast]);

  useEffect(() => {
    if (autoShownRef.current) {
      return;
    }
    const query = readInitialQuery();
    if (!query.shouldAutoShow) {
      return;
    }
    autoShownRef.current = true;
    if (query.preset) {
      applyPreset(query.preset);
      return;
    }
    showToast(query.action, query.message, query.title, query.duration);
  }, [applyPreset, showToast]);

  const handleShowToast = () => {
    const durationMs = Number.parseInt(duration, 10);
    showToast(
      action,
      message,
      title,
      Number.isNaN(durationMs) ? TOAST_LAB_DEFAULT_DURATION_MS : durationMs,
    );
  };

  return (
    <Box flex={1} width="100%">
      <VStack space="lg" px="$5" py="$6" maxWidth={960} alignSelf="center" width="100%">
        <VStack space="xs">
          <Heading size="xl">Toast Lab</Heading>
          <Text size="sm" color="$textSubtle">
            Fire production portal toasts on a loud canvas. Default duration is 60 seconds for screenshots.
          </Text>
        </VStack>

        <Box
          testID="toast-lab-contrast-canvas"
          borderWidth={2}
          borderColor="$borderStrong"
          borderRadius="$lg"
          overflow="hidden"
          minHeight={280}
          position="relative"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            sx={{
              _web: {
                backgroundImage: `
                  linear-gradient(45deg, #ff6b6b 25%, transparent 25%),
                  linear-gradient(-45deg, #ff6b6b 25%, transparent 25%),
                  linear-gradient(45deg, transparent 75%, #4ecdc4 75%),
                  linear-gradient(-45deg, transparent 75%, #ffe66d 75%)
                `,
                backgroundSize: '40px 40px',
                backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px',
                backgroundColor: '#f7fff7',
              },
            }}
          />
          <VStack space="sm" p="$5" position="relative" zIndex={1}>
            <Text size="md" color="$textHeading" fontWeight="semibold">
              Contrast canvas
            </Text>
            <Text size="sm" color="$textBody" maxWidth={520}>
              Toast renders in the app portal above this grid. If message text paints past the toast border,
              it will be obvious here.
            </Text>
          </VStack>
        </Box>

        <VStack space="md">
          <ControlGroup label="Presets">
            <HStack space="xs" flexWrap="wrap">
              {PRESET_IDS.map((presetId) => (
                <PresetChip
                  key={presetId}
                  label={presetId}
                  active={activePreset === presetId}
                  onPress={() => applyPreset(presetId)}
                />
              ))}
            </HStack>
          </ControlGroup>

          <HStack space="md" flexWrap="wrap">
            <ControlGroup label="Action">
              <HStack space="xs" flexWrap="wrap">
                {TOAST_ACTIONS.map((nextAction) => (
                  <PresetChip
                    key={nextAction}
                    label={nextAction}
                    active={action === nextAction}
                    onPress={() => setAction(nextAction)}
                  />
                ))}
              </HStack>
            </ControlGroup>

            <ControlGroup label="Duration (ms)">
              <Input size="md">
                <InputField
                  value={duration}
                  onChangeText={setDuration}
                  keyboardType="numeric"
                  placeholder={String(TOAST_LAB_DEFAULT_DURATION_MS)}
                />
              </Input>
            </ControlGroup>
          </HStack>

          <ControlGroup label="Title">
            <Input size="md">
              <InputField
                value={title}
                onChangeText={setTitle}
                placeholder="Optional toast title"
              />
            </Input>
          </ControlGroup>

          <ControlGroup label="Message">
            <Textarea size="md">
              <TextareaInput
                value={message}
                onChangeText={setMessage}
                placeholder="Toast message body"
              />
            </Textarea>
          </ControlGroup>

          <Button action="primary" alignSelf="flex-start" onPress={handleShowToast}>
            <ButtonText>Show toast</ButtonText>
          </Button>
        </VStack>
      </VStack>
    </Box>
  );
}
